// Device and phone-specific tracking — optimized for Senegal market
import { useEffect, useRef, useCallback, useState } from "react";
import { trackEvent } from "@/lib/analytics";

// ── Device Info ─────────────────────────────────────────────────────────────

export function useDeviceTracking(): void {
  useEffect(() => {
    const nav = navigator as Navigator & {
      connection?: { effectiveType?: string; downlink?: number };
    };

    const isPwa = window.matchMedia("(display-mode: standalone)").matches
      || (navigator as unknown as { standalone?: boolean }).standalone === true;

    trackEvent("device_info", {
      device_type: window.innerWidth < 768 ? "mobile" : window.innerWidth < 1024 ? "tablet" : "desktop",
      os: navigator.platform,
      browser: navigator.userAgent.slice(0, 100),
      screen_width: window.screen.width,
      screen_height: window.screen.height,
      pixel_ratio: window.devicePixelRatio,
      connection_type: nav.connection?.effectiveType ?? "unknown",
      is_pwa: isPwa,
      language: navigator.language,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    });
  }, []);
}

// ── Connection Quality ──────────────────────────────────────────────────────

export function useConnectionQuality() {
  const [isOffline, setIsOffline] = useState(!navigator.onLine);
  const prevTypeRef = useRef("unknown");

  useEffect(() => {
    const onOnline = () => {
      setIsOffline(false);
      trackEvent("came_online", {});
    };
    const onOffline = () => {
      setIsOffline(true);
      trackEvent("went_offline", {});
    };

    const nav = navigator as Navigator & {
      connection?: EventTarget & { effectiveType?: string; addEventListener: (t: string, l: () => void) => void; removeEventListener: (t: string, l: () => void) => void };
    };

    const onConnectionChange = () => {
      const newType = nav.connection?.effectiveType ?? "unknown";
      if (newType !== prevTypeRef.current) {
        trackEvent("connection_changed", {
          previous_type: prevTypeRef.current,
          new_type: newType,
          is_slow: newType === "2g" || newType === "slow-2g",
        });
        prevTypeRef.current = newType;
      }
    };

    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOffline);
    nav.connection?.addEventListener("change", onConnectionChange);

    return () => {
      window.removeEventListener("online", onOnline);
      window.removeEventListener("offline", onOffline);
      nav.connection?.removeEventListener("change", onConnectionChange);
    };
  }, []);

  return {
    isOffline,
    isSlowConnection: prevTypeRef.current === "2g" || prevTypeRef.current === "slow-2g",
    connectionType: prevTypeRef.current,
  };
}

// ── Phone Interactions ──────────────────────────────────────────────────────

export function usePhoneInteraction(): void {
  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const link = target.closest("a");
      if (!link) return;

      const href = link.getAttribute("href") ?? "";
      if (href.startsWith("tel:")) {
        trackEvent("phone_call_initiated", {
          page_source: window.location.pathname,
          context: "general",
        });
      } else if (href.includes("wa.me") || href.includes("whatsapp")) {
        trackEvent("whatsapp_opened", {
          page_source: window.location.pathname,
        });
      }
    };

    document.addEventListener("click", onClick);
    return () => document.removeEventListener("click", onClick);
  }, []);
}

// ── PWA Tracking ────────────────────────────────────────────────────────────

export function usePWATracking(): void {
  useEffect(() => {
    const onBeforeInstallPrompt = () => {
      trackEvent("pwa_install_prompt_shown", {});
    };

    const onAppInstalled = () => {
      trackEvent("pwa_installed", {});
    };

    window.addEventListener("beforeinstallprompt", onBeforeInstallPrompt);
    window.addEventListener("appinstalled", onAppInstalled);

    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstallPrompt);
      window.removeEventListener("appinstalled", onAppInstalled);
    };
  }, []);
}
